import { useState, useEffect, useCallback } from "react";
import { getAssessment, getAllAssessmentQuestions } from "../services/api";
import { useDispatch } from "react-redux";
import { loadQuestions, loadData } from "../store/assessmentSlice";
import { notify } from "../utils/notification";
import { clearTimersExcept } from "../utils/timerStorage";

export const useFetchAssessment = (id) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    if (id) {
      try {
        const data = await getAssessment(id);
        const qData = await getAllAssessmentQuestions(id);

        // Starting an exam with part of its paper is worse than not starting it: the
        // server still marks the attempt out of every question, so the missing ones count
        // against the student. Say so rather than open a short exam quietly.
        if (!qData || qData.result.length < qData.totalItemCount) {
          throw new Error(
            `Loaded ${qData?.result.length ?? 0} of ${
              qData?.totalItemCount ?? 0
            } questions`
          );
        }

        dispatch(loadQuestions(qData.result));
        dispatch(loadData(data.result));

        // CR-03: the timers used to be wiped by a localStorage.clear() on beforeunload,
        // left behind as debug code, which also threw away everything else the app had
        // stored. Now that each clock is keyed to its own exam, only the other exams'
        // clocks are dropped.
        clearTimersExcept(data.result?.id);
      } catch (e) {
        console.error(e);
        setError(true);
        notify("There was an issue fetching the assessment data.");
        // Further enhancement: Use toast or notification to show this error to the user.
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setError(true);
    }
  }, [id, dispatch]);

  // Exam data
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { isLoading, error };
};
